/**
 * THE LOADING SCREEN OF A ROUTE'S GUARD, IN THE LIGHT OF THE PAGE BEHIND IT (D-92 part 3, Miles 2026-10-24: "ensure to
 * be thorough … no matter what the process/transition … and not matter what mode the user is in").
 *
 * App.tsx's guards (the sign-in check, the developer and permission gates) show "one moment…" while they decide, and
 * the justice page's gate shows the staged loading screen while sign-in settles. In front of a page of the new design
 * at dusk that screen was noon: on every full load of a signed-in page (a reload, a link opened in a new tab, a
 * workspace switch) and on the way in from a public page, a noon screen stood between the boot splash and the page.
 *
 * Each guard hands its loading screen the children it guards. The screen finds the page among them
 * (src/design/pageAhead.ts) and, where that page's design shows, waits in the light the page opens in, as the page's
 * door does after it (src/design/pavilion/waitingLight.ts): it says the light on <html> (the document's ground, the chat
 * and the overlays read it), hands it with its switch to the app header in front of a page under it, and draws the
 * screen at dusk. In front of one of today's pages it is the screen it always was.
 *
 * Mounted only while its guard waits, so whatever it says is released the moment the page takes over, in the same
 * commit the page says its own.
 */
import React from 'react';
import BrandedTransition from '../common/BrandedTransition';
import ResilientSuspenseFallback from '../ResilientSuspenseFallback';
import { PageLight } from '../../design/PageLight';
import { designShows } from '../../design/previewPath';
import { pavilionPageAhead } from '../../design/pageAhead';
import { useWaitingLight } from '../../design/pavilion/waitingLight';
import type { PreviewTheme } from '../../design/previewTheme';

/** The light to wait in, in front of `ahead`: the page's own where its design shows; null in front of today's pages. */
function useGuardLight(ahead: React.ReactNode): PreviewTheme | null {
  const page = pavilionPageAhead(ahead);
  const shows = page !== null && designShows(page.key);
  const theme = useWaitingLight(shows && page !== null && page.appChrome, shows);
  return shows ? theme : null;
}

export interface GuardLoadingProps {
  /** What the guard stands in front of: the children it was handed (the page, perhaps inside another guard). */
  ahead: React.ReactNode;
  /** The status line (house style: lowercase, never a URL). */
  title: string;
}

/** App.tsx's guards: the branded beat. */
export const GuardLoading: React.FC<GuardLoadingProps> = ({ ahead, title }) => {
  const light = useGuardLight(ahead);
  return (
    <>
      {light && <PageLight theme={light} />}
      <BrandedTransition variant="page" title={title} light={light} />
    </>
  );
};

export interface GuardStagedLoadingProps {
  /** What the guard stands in front of: the children it was handed. */
  ahead: React.ReactNode;
  /** The staged screen's name in its console warning. */
  gateName: string;
}

/** The justice page's gate: the staged loading screen (loading…, still loading…, then refresh), in the same light. */
export const GuardStagedLoading: React.FC<GuardStagedLoadingProps> = ({ ahead, gateName }) => {
  const light = useGuardLight(ahead);
  return (
    <>
      {light && <PageLight theme={light} />}
      <ResilientSuspenseFallback gateName={gateName} light={light} />
    </>
  );
};

export default GuardLoading;
